import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../layouts/AdminLayout';
import { templateService, Template } from '../../services/api';
import TemplatePreview from './TemplatePreview';
import './GeneraBuono.css';

const GeneraBuono: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [logoErrors, setLogoErrors] = useState<Record<string, boolean>>({});
  const [frontImageErrors, setFrontImageErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        const data = await templateService.getTemplates();
        const sorted = [...data].sort((a, b) => {
          if (a.slug === 'template_1' && b.slug !== 'template_1') return -1;
          if (a.slug !== 'template_1' && b.slug === 'template_1') return 1;
          return 0;
        });
        setTemplates(sorted);
        setSelectedIndex(0);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Errore nel caricamento dei template');
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  const selectedTemplate = templates[selectedIndex] ?? null;
  const selectedTemplateId = selectedTemplate?.id ?? null;

  const goPrev = () => {
    setSelectedIndex((i) => (i <= 0 ? templates.length - 1 : i - 1));
  };

  const goNext = () => {
    setSelectedIndex((i) => (i >= templates.length - 1 ? 0 : i + 1));
  };

  const handleContinue = () => {
    if (selectedTemplateId != null) {
      navigate('/admin/genera-buono/crea', { state: { templateId: selectedTemplateId }, replace: false });
    }
  };

  const handleSelectTemplate = (templateId: number) => {
    navigate('/admin/genera-buono/crea', { state: { templateId }, replace: false });
  };

  const resolveImageUrl = (value?: string | null) => {
    if (!value) return '';
    if (/^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:') || value.startsWith('/')) {
      return value;
    }
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
    return `${apiBase}/storage/${value}`;
  };

  return (
    <AdminLayout activeSection="genera-buono">
      <div className="genera-page d-flex flex-column flex-grow-1">
        <div className="genera-header">
          <h1>Crea il tuo buono</h1>
          <p>Scegli il template</p>
        </div>

        {loading && <div className="genera-loading">Caricamento...</div>}
        {error && <div className="genera-error">{error}</div>}

        {!loading && !error && templates.length > 0 && (
          <>
            {/* Vista estesa: due card affiancate (come prima) */}
            <div className="genera-templates genera-templates-extended d-flex justify-content-between flex-wrap gap-3">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className="genera-card d-flex flex-column align-items-stretch justify-content-center"
                  onClick={() => handleSelectTemplate(template.id)}
                >
                  <TemplatePreview
                    template={template}
                    resolveImageUrl={resolveImageUrl}
                    logoErrors={logoErrors}
                    frontImageErrors={frontImageErrors}
                  />
                </button>
              ))}
            </div>

            {/* Vista compatta: frecce + testo sopra, anteprima sotto */}
            <div className="genera-template-choice">
              <div className="genera-template-nav">
                <button
                  type="button"
                  className="genera-template-arrow genera-template-arrow-prev"
                  onClick={goPrev}
                  aria-label="Template precedente"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <span className="genera-template-nav-labels">
                  {selectedTemplate?.slug === 'template_1' ? 'nuovo' : selectedTemplate?.slug === 'template_2' ? 'vecchio' : ''}
                </span>
                <button
                  type="button"
                  className="genera-template-arrow genera-template-arrow-next"
                  onClick={goNext}
                  aria-label="Template successivo"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              <div className="genera-template-carousel">
                {selectedTemplate && (
                  <div className="genera-card d-flex flex-column align-items-stretch justify-content-center">
                    <div className="genera-template-preview-wrap">
                      <TemplatePreview
                        template={selectedTemplate}
                        resolveImageUrl={resolveImageUrl}
                        logoErrors={logoErrors}
                        frontImageErrors={frontImageErrors}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="genera-template-actions">
                <button
                  type="button"
                  className="genera-template-btn-continua"
                  onClick={handleContinue}
                  disabled={selectedTemplateId == null}
                >
                  Continua con questo template
                </button>
              </div>
            </div>
          </>
        )}
        <div className="genera-footer d-flex flex-column align-items-center gap-1 pt-2">
          <span className="text-center">© 2021 All rights reserved Estetica Cruciani</span>
        </div>
      </div>
    </AdminLayout>
  );
};

export default GeneraBuono;
